/**
 * Verbes métier du domaine client.
 */
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ClientRepository } from "./repository.js";
import { computeOutstanding } from "./types.js";
import type { Client, ClientDetail } from "./types.js";
import type {
  ArchiveClientInput,
  ListClientsInput,
  ReadClientInput,
  SaveClientInput,
} from "./schema.js";

export function createClientServices(repo: ClientRepository) {
  const listerClients = defineService<ListClientsInput, Client[]>({
    name: "lister_clients",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.list(ctx, { search: input.search, type: input.type }),
  });

  const lireClient = defineService<ReadClientInput, ClientDetail>({
    name: "lire_client",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const found = await repo.findById(ctx, input.clientId);
      if (!found) {
        throw new DomainError("not_found", "Ce client n'existe pas.", {
          clientId: input.clientId,
        });
      }
      // L'encours se déduit des factures : le stocker créerait une valeur à
      // resynchroniser à chaque encaissement, donc une valeur fausse un jour.
      return { ...found, outstanding: computeOutstanding(found.invoices) };
    },
  });

  const enregistrerClient = defineService<SaveClientInput, Client>({
    name: "enregistrer_client",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) =>
      repo.save(ctx, {
        clientId: input.clientId,
        type: input.type,
        firstName: input.firstName ?? null,
        lastName: input.lastName ?? null,
        companyName: input.companyName ?? null,
        siret: input.siret ?? null,
        email: input.email ?? null,
        phone: input.phone ?? null,
        phoneSecondary: input.phoneSecondary ?? null,
        addressLine1: input.addressLine1 ?? null,
        postalCode: input.postalCode ?? null,
        city: input.city ?? null,
        notes: input.notes ?? null,
        isPublicEntity: input.isPublicEntity,
        chorusProStructureId: input.chorusProStructureId ?? null,
      }),
  });

  const archiverClient = defineService<ArchiveClientInput, { clientId: string }>({
    name: "archiver_client",
    // Réversible — on pose une date d'archivage, on n'efface rien. Une vraie
    // suppression serait N3 : elle emporterait l'historique comptable.
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => {
      const found = await repo.findById(ctx, input.clientId);
      if (!found) {
        throw new DomainError("not_found", "Ce client n'existe pas.", {
          clientId: input.clientId,
        });
      }

      const { totalDue } = computeOutstanding(found.invoices);
      if (totalDue > 0) {
        throw new DomainError(
          "conflict",
          `Ce client doit encore ${totalDue.toFixed(2)} €. ` +
            `Archivez-le une fois les factures soldées, sinon l'impayé disparaît des relances.`,
          { clientId: input.clientId, totalDue },
        );
      }

      await repo.archive(ctx, input.clientId);
      return { clientId: input.clientId };
    },
  });

  return { listerClients, lireClient, enregistrerClient, archiverClient };
}

export type ClientServices = ReturnType<typeof createClientServices>;
